//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import dayjs from 'dayjs';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';

// === COMPONENTS ===
import { useFile } from '../../../../../FileContext/FileContext';
import Styled from 'components/Styled/Styled';
import { Header } from 'components/Collapsible';
import Doctype  from './components/Doctype/Doctype';
import Standard from './components/Standard/Standard';

// === API ===
import { emit, on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';
import useOnVisible from 'components/hooks/useOnVisible/useOnVisible';

//#endregion

//-----------------------------------------------------------------
export default function Field( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { id, is_comp } = props;

  const theme = useTheme();

  const { file_id } = useFile();
  
  const [ field   , setField   ] = useState({});
  const [ focused , setFocused ] = useState(false);

  const length_char = field.display_info ? + field.display_info.length : 50;
  const length      = field.display_info ? Math.max(length_char * 12, 20 )+'px' : '100%' ;

  const ref = useRef(null);
  const ref_input = useRef();

  const [is_visible] = useOnVisible(ref);

  //#endregion

  //#region [STYLES]


  //#endregion
    
  //#region [EFFECT]
  
  useEffect( () => {
    const FIELD = is_comp ? 'FILE_COMPARISON_FIELD' : 'FILE_FIELD';
    
    //-----------------------------------------------------------------
    function handleField( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setField( data );
      if( !data.is_selected || ref_input.current == null ) return;
      ref_input.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
      ref_input.current.focus();
    }

    on( FIELD, handleField );

    return () => {
      removeListener( FIELD , handleField );
    }
  }, [id, ref_input] )

  useEffect( () => {
    if( !is_visible ) return;
    const type = is_comp ? 'FILE_REQUEST_COMPARISON_FIELD' : 'FILE_REQUEST_FIELD';
    sendMessageData( type, { file_id, field_id: id } );
  }, [id,is_visible] )
  
  useEffect( () => {
    if( ref_input.current == null ) return;
    //-----------------------------------------------------------------
    function handleFieldFocus(data) {
      //-----------------------------------------------------------------
      if( data == undefined && focused ) {
        ref_input.current.scrollIntoView({ behavior: "instant", block: "center", inline: "nearest" });
        ref_input.current.focus();
        return;
      }
      
      if( data == undefined ) return;

      if( data.id !== id && focused ) {
        setFocused( false );
      };
      if( data.id !== id ) return;
      setFocused( true );
      ref_input.current.focus();
    }
    //-----------------------------------------------------------------
    function handleFieldFocusOther(data) {
      //-----------------------------------------------------------------
      if( focused ) {
        setFocused( false );
      };
    }

    const FIELD_FOCUS       = is_comp ? 'FILE_COMPARISON_FIELD_FOCUS' : 'FILE_FIELD_FOCUS';
    const FIELD_FOCUS_OTHER = is_comp ? 'FILE_FIELD_FOCUS'            : 'FILE_COMPARISON_FIELD_FOCUS';
    on( FIELD_FOCUS      , handleFieldFocus      );
    on( FIELD_FOCUS_OTHER, handleFieldFocusOther );

    return () => {
      removeListener( FIELD_FOCUS      , handleFieldFocus      );
      removeListener( FIELD_FOCUS_OTHER, handleFieldFocusOther );
    }
  }, [focused, ref_input.current, id] )
  
  useEffect( () => {
  }, [field?.value] )

  //#endregion
  
  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onFocus() {
    //-----------------------------------------------------------------
    if( focused ) return;
    const type = is_comp ? 'FILE_COMPARISON_FIELD_SELECT' : 'FILE_FIELD_SELECT';
    sendMessageData( type, { file_id, fields: [id], is_selected: true } )
  }

  //#endregion

  //#region

  //-----------------------------------------------------------------
  function onChange( value ) {
    //-----------------------------------------------------------------
    sendMessageData( 'FILE_FIELD_UPDATE', { file_id, field: { id, value } } )
  }

  //#endregion

  //#region [RENDER]

  const color         = is_comp ? theme.palette.secondary.main : '#ffa500';
  const border_pd     = { border: '2px solid ' + theme.palette.primary.main, borderRadius: '5px'};
  const border_focus  = { border: '2px solid ' + color, borderRadius: '5px'};
  const _border       = focused ? border_focus : border_pd;
  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    const header = field?.display_info?.header || '';
    return (
      <Styled ref={ref} styles={[{minHeight:'70px', width: 'max-content'}, { paddingLeft: '24px' }]} >
        {
          field == null || !is_visible? (
           <div>Loading...</div> 
          ) : (
            <>
              <Header styles={[ {} ]} height={header==='' ? 0 : 30} title={header} />
              <Styled styles={[ _border,{width: length,height:'32px'}]}>
                {displayContent()}
              </Styled>
              <Styled styles={[{ overflow: 'auto' }]}>
                {focused ? displayHistory() : null}
              </Styled>
            </>
          )
        }
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function displayContent() {
    //-----------------------------------------------------------------
    if( !field ) return;

    if( field.display_info?.type === 'doctype' ) {
      return <Doctype ref={ref_input} content={field?.value || ''} tenant={field.tenant || ''} length={length} handleFocus={onFocus} handleChange={onChange} />
    }

    return (
      <Standard ref={ref_input} content={field?.value||''} length={length} handleFocus={onFocus} handleChange={onChange} />
    )
  }

  //-----------------------------------------------------------------
  function displayHistory() {
    //-----------------------------------------------------------------
    if( field.history?.length <= 0 ) {
      return (
      <Styled styles={[{padding:'20px'}]}>
        No field history.
      </Styled>
      )
    }
    const history = field.history?.map( (x,index)=>(
      [
        <div key={'stage-'+index} style={{ minHeight: '30px' }}>{x.stage||''}</div>,
        <div key={'value-'+index} style={{ wordBreak: 'break-word' }}>{x.value||''}</div>,
        <div key={'user-' +index} style={{ minHeight: '30px' }}>{x.user||''}</div>,
        <div key={'date-' +index} style={{ }}>{dayjs(x.timestamp).format('YYYY-MM-DD HH:mm A')||''}</div>,
      ]
    ))||null
    return (
      <Styled styles={[{display:'grid', gridTemplateColumns: '60px minmax(50px, auto) minmax(50px, auto) auto', gridTemplateRows: `30px repeat( ${history.length-1||0}, minmax( 30px, auto ) )`, columnGap: '12px', rowGap: '4px'},{padding: '12px'}, { overfow: 'auto' }, { justifyContent: '' }, ]}>
        {[<div>Stage</div>,<div>Value</div>,<div>User</div>,<div>Date</div>]}
        {history}
      </Styled>
    )
  }

  //#endregion

  return display();
}